import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import type { AssistantCommandView, OeePeriod } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { useOee } from "@/features/oee/hooks";
import { useActiveRun } from "@/features/production/hooks";

// Comandos `display` executam NO CLIENTE (canal 2 LGPD): o dado completo vem
// banco→tela pelos hooks/sessão do usuário. `inline` = card compacto na bolha; `panel` = deep-link
// para a tela existente (a área principal continua visível ao lado do dock — reflow). Os resolvers
// respeitam os args do catálogo (período/filtros) — não os descartam.

const fmtPct = (v: number | null): string => (v == null ? "—" : `${v.toFixed(1)}%`);

const OEE_PERIODS: OeePeriod[] = ["8h", "24h", "7d", "30d"];
const PERIOD_LABEL: Record<OeePeriod, string> = {
  "8h": "últimas 8h",
  "24h": "últimas 24h",
  "7d": "últimos 7 dias",
  "30d": "últimos 30 dias",
};
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
// Query-string só com os args definidos (deep-link carrega os filtros do comando).
const toSearch = (params: Record<string, string | undefined>): string => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const s = qs.toString();
  return s ? `?${s}` : "";
};

function OeeInline({
  lineId,
  period,
  from,
  to,
  compareWithPrevious,
}: {
  lineId: string;
  period?: OeePeriod;
  from?: string;
  to?: string;
  compareWithPrevious?: boolean;
}) {
  const window = from && to ? { from, to } : undefined;
  const q = useOee(lineId, period ?? "24h", {
    compareWithPrevious: compareWithPrevious ?? true,
    window,
  });
  const label = window ? "período personalizado" : PERIOD_LABEL[period ?? "24h"];
  if (q.isError) return <Notify.QueryError error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="rounded-md border border-border bg-background p-2.5">
      <div className="mb-1 text-xs font-medium text-muted-foreground">OEE — {label}</div>
      {q.isLoading || !q.data ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-lg font-semibold tabular-nums text-foreground">
            {fmtPct(q.data.oee)}
          </span>
          <span className="text-xs text-muted-foreground">
            A {fmtPct(q.data.availability)} · P {fmtPct(q.data.performance)} · Q{" "}
            {fmtPct(q.data.quality)}
          </span>
        </div>
      )}
    </div>
  );
}

const PROD_STATE: Record<string, string> = {
  ACTIVE: "Em produção",
  PAUSED: "Pausada",
  COMPLETED: "Encerrada",
};

function ProductionInline({ lineId }: { lineId: string }) {
  const q = useActiveRun(lineId);
  if (q.isError) return <Notify.QueryError error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading) return <div className="text-sm text-muted-foreground">Carregando…</div>;
  const run = q.data;
  if (!run)
    return (
      <div className="rounded-md border border-border bg-background p-2.5 text-sm text-muted-foreground">
        Nenhuma produção ativa nesta linha.
      </div>
    );
  const pct = run.plannedQty > 0 ? Math.round((run.producedQty / run.plannedQty) * 100) : 0;
  return (
    <div className="rounded-md border border-border bg-background p-2.5">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span className="inline-block h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
        {PROD_STATE[run.status] ?? run.status}
      </div>
      <div className="text-sm text-foreground">
        <span className="font-semibold tabular-nums">
          {run.producedQty.toLocaleString("pt-BR")}
        </span>
        <span className="text-muted-foreground">
          {" "}
          / {run.plannedQty.toLocaleString("pt-BR")} un ·
        </span>{" "}
        <span className="tabular-nums">{pct}%</span>
      </div>
    </div>
  );
}

function DeepLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(to)}>
      <ExternalLink className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

// Resolver por comando display (invariante 3 do contrato: os 8 têm resolver).
export function DisplayCard({ command }: { command: AssistantCommandView }) {
  const args = command.argsJson;
  const lineId = str(args.lineId);
  const machineId = str(args.machineId);
  switch (command.name) {
    case "oee.show":
      return lineId ? (
        <OeeInline
          lineId={lineId}
          period={
            OEE_PERIODS.includes(args.period as OeePeriod) ? (args.period as OeePeriod) : undefined
          }
          from={str(args.from)}
          to={str(args.to)}
          compareWithPrevious={
            typeof args.compareWithPrevious === "boolean" ? args.compareWithPrevious : undefined
          }
        />
      ) : null;
    case "production.showStatus":
      return lineId ? <ProductionInline lineId={lineId} /> : null;
    case "alarms.show":
      // /alarms carrega os filtros do comando via search params.
      return (
        <DeepLink
          to={`/alarms${toSearch({ lineId, state: str(args.state), severity: str(args.severity) })}`}
          label="Ver alarmes"
        />
      );
    case "machineEvents.show":
      // Rota real da linha é /lines/:id/detail; carrega máquina/intervalo como filtros.
      return (
        <DeepLink
          to={
            lineId
              ? `/lines/${lineId}/detail${toSearch({ machineId, from: str(args.from), to: str(args.to) })}`
              : "/lines"
          }
          label="Ver eventos"
        />
      );
    case "line.showList":
      return <DeepLink to="/lines" label="Ver linhas" />;
    case "dashboard.showOverview":
      return <DeepLink to="/" label="Ver painel geral" />;
    case "simulation.showResult":
      return (
        <DeepLink
          to={str(args.simulationId) ? `/simulations/${str(args.simulationId)}` : "/simulations"}
          label="Ver resultado da simulação"
        />
      );
    case "simulation.compare":
      // A tela de comparação recebe os dois ids por search params.
      return (
        <DeepLink
          to={`/simulations/compare${toSearch({ a: str(args.a), b: str(args.b) })}`}
          label="Ver comparação"
        />
      );
    default:
      return null;
  }
}
